import React, { useState, useEffect, useCallback } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import VideocamIcon from '@mui/icons-material/Videocam';
import AdLibraryService from 'services/AdLibraryService';
import socket from 'services/socket';
import { ScrapeStage, ScrapeProgressEvent, ScrapeProgressAd } from './types';

const service = new AdLibraryService();

function toYMD(d: Date) { return d.toISOString().split('T')[0]; }
function daysAgo(n: number) { const d = new Date(); d.setDate(d.getDate() - n); return toYMD(d); }
function startOfYear() { return `${new Date().getFullYear()}-01-01`; }

const DATE_PRESETS: Array<{ label: string; from: string; to: string }> = [
  { label: 'All Time', from: '', to: '' },
  { label: 'Last Week', from: daysAgo(7), to: toYMD(new Date()) },
  { label: 'Last Month', from: daysAgo(30), to: toYMD(new Date()) },
  { label: 'Last 3 Months', from: daysAgo(90), to: toYMD(new Date()) },
  { label: 'This Year', from: startOfYear(), to: toYMD(new Date()) },
];

const STEPS = ['Launching Browser', 'Loading Ad Library', 'Fetching Ads', 'Saving to Database', 'Complete'];

function stageToStep(stage: ScrapeStage | null): number {
  switch (stage) {
    case 'browserLaunching': return 0;
    case 'pageLoading': return 1;
    case 'collecting': return 2;
    case 'saving': return 3;
    case 'done': return 5; // past last index = all complete
    case 'error': return -1;
    default: return -1;
  }
}

interface ScrapeProgressDialogProps {
  open: boolean;
  onClose: () => void;
  onKeywordSaved: () => void;
  initialKeyword?: { keyword: string; scrapeDepth: number; keywordId: string; startDateMin?: string | null; startDateMax?: string | null } | null;
}

export default function ScrapeProgressDialog({ open, onClose, onKeywordSaved, initialKeyword }: ScrapeProgressDialogProps) {
  // Form state
  const [keyword, setKeyword] = useState('');
  const [scrapeDepth, setScrapeDepth] = useState(20);
  const [datePreset, setDatePreset] = useState('All Time');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Progress state
  const [scrapeRunId, setScrapeRunId] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState<ScrapeStage | null>(null);
  // Remembers the stage that was active when an `error` event arrives, so
  // the error indicator lands on the step that actually failed (e.g. step 0
  // for a browser-launch failure) instead of a hardcoded default.
  const [errorStage, setErrorStage] = useState<ScrapeStage | null>(null);
  const [collectedAds, setCollectedAds] = useState<ScrapeProgressAd[]>([]);
  const [adsFound, setAdsFound] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchUrl, setSearchUrl] = useState<string | null>(null);
  const [urlRewritten, setUrlRewritten] = useState(false);

  // Dedup stats
  const [insertedCount, setInsertedCount] = useState(0);
  const [refreshedCount, setRefreshedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);

  // Save keyword state
  const [keywordSaved, setKeywordSaved] = useState(false);
  const [savingKeyword, setSavingKeyword] = useState(false);

  const isRunning = scrapeRunId !== null && currentStage !== 'done' && currentStage !== 'error';
  const isDone = currentStage === 'done';
  const isError = currentStage === 'error';
  const activeStep = stageToStep(currentStage);

  // Pre-fill from initialKeyword and auto-start
  useEffect(() => {
    if (open && initialKeyword) {
      setKeyword(initialKeyword.keyword);
      setScrapeDepth(initialKeyword.scrapeDepth);
      if (initialKeyword.startDateMin || initialKeyword.startDateMax) {
        setDatePreset('Custom');
        setDateFrom(initialKeyword.startDateMin || '');
        setDateTo(initialKeyword.startDateMax || '');
      }
    }
  }, [open, initialKeyword]);

  // Socket.IO listener
  useEffect(() => {
    if (!scrapeRunId) return;

    socket.emit('joinScrapeRoom', { scrapeRunId });

    const handler = (data: ScrapeProgressEvent) => {
      if (data.scrapeRunId !== scrapeRunId) return;

      if (data.stage === 'error') {
        // Capture the stage we were on before flipping — used to highlight
        // the failed step in the stepper. Don't overwrite if `error` arrives
        // again without an intervening progress event.
        setErrorStage(prev => prev ?? (currentStage as ScrapeStage | null));
        setErrorMessage(data.error || 'Unknown error');
        setCurrentStage('error');
        return;
      }

      setCurrentStage(data.stage);
      if (data.searchUrl) {
        setSearchUrl(data.searchUrl);
        setUrlRewritten(!!data.urlRewritten);
      }

      if (data.stage === 'collecting') {
        if (data.newAds && data.newAds.length > 0) {
          setCollectedAds(prev => [...prev, ...data.newAds!]);
        }
        if (data.adsFound != null) setAdsFound(data.adsFound);
      }

      if (data.stage === 'done') {
        if (data.totalAds != null) setAdsFound(data.totalAds);
        if (data.inserted != null) setInsertedCount(data.inserted);
        if (data.refreshed != null) setRefreshedCount(data.refreshed);
        if (data.skipped != null) setSkippedCount(data.skipped);
      }
    };

    socket.on('scrapeProgress', handler);
    return () => { socket.off('scrapeProgress', handler); };
  }, [scrapeRunId]);

  const handleStartScrape = useCallback(async () => {
    if (!keyword.trim()) return;

    setCollectedAds([]);
    setAdsFound(0);
    setInsertedCount(0);
    setRefreshedCount(0);
    setSkippedCount(0);
    setErrorMessage(null);
    setErrorStage(null);
    setKeywordSaved(false);
    setCurrentStage('browserLaunching');

    const params: any = {
      keyword: keyword.trim(),
      scrapeDepth,
      startDateMin: dateFrom || undefined,
      startDateMax: dateTo || undefined,
    };

    if (initialKeyword?.keywordId) {
      params.keywordId = initialKeyword.keywordId;
    }

    const result = await service.triggerScrape(params);
    if (result?.scrapeRunId) {
      setScrapeRunId(result.scrapeRunId);
    }
  }, [keyword, scrapeDepth, dateFrom, dateTo, initialKeyword]);

  // Auto-start for saved keywords
  useEffect(() => {
    if (open && initialKeyword && !scrapeRunId) {
      handleStartScrape();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialKeyword]);

  const handleSaveKeyword = async () => {
    setSavingKeyword(true);
    await service.createKeyword({
      keyword: keyword.trim(),
      scrapeDepth,
      startDateMin: dateFrom || null,
      startDateMax: dateTo || null,
    });
    setKeywordSaved(true);
    setSavingKeyword(false);
    onKeywordSaved();
  };

  const handleClose = () => {
    if (!isRunning) {
      // Reset all state
      setScrapeRunId(null);
      setCurrentStage(null);
      setCollectedAds([]);
      setAdsFound(0);
      setInsertedCount(0);
      setRefreshedCount(0);
      setSkippedCount(0);
      setErrorMessage(null);
      setErrorStage(null);
      setKeywordSaved(false);
      if (!initialKeyword) {
        setKeyword('');
        setScrapeDepth(20);
        setDatePreset('All Time');
        setDateFrom('');
        setDateTo('');
      }
      onClose();
    }
  };

  const showForm = !scrapeRunId && !currentStage;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={showForm ? 'sm' : 'md'} fullWidth>
      <DialogTitle>{showForm ? 'Fetch Competitor Ads' : `Fetching: "${keyword}"`}</DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        {showForm ? (
          /* ── Phase 1: Input Form ── */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField size="small" label="Keyword" fullWidth value={keyword}
              onChange={e => setKeyword(e.target.value)} placeholder="e.g., sleep supplement" autoFocus />
            <TextField size="small" label="Max Ads" type="number" value={scrapeDepth}
              onChange={e => setScrapeDepth(parseInt(e.target.value, 10) || 20)} sx={{ width: 140 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Date Range:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {DATE_PRESETS.map(p => (
                  <Chip key={p.label} label={p.label} size="small"
                    color={datePreset === p.label ? 'primary' : 'default'}
                    variant={datePreset === p.label ? 'filled' : 'outlined'}
                    onClick={() => { setDatePreset(p.label); setDateFrom(p.from); setDateTo(p.to); }}
                  />
                ))}
                <Chip label="Custom" size="small"
                  color={datePreset === 'Custom' ? 'primary' : 'default'}
                  variant={datePreset === 'Custom' ? 'filled' : 'outlined'}
                  onClick={() => setDatePreset('Custom')}
                />
              </Box>
              {datePreset === 'Custom' && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" type="date" label="From" value={dateFrom}
                    onChange={e => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                  <TextField size="small" type="date" label="To" value={dateTo}
                    onChange={e => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          /* ── Phase 2/3: Progress + Results ── */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* The exact search being run, so the filters can be checked against
                the real thing. A dropped page_ids param still returns plenty of
                ads — just the wrong ones — so this is the only cheap way to see
                that the fetch searched what you asked it to. */}
            {searchUrl && (
              <Alert severity={urlRewritten ? 'warning' : 'info'} sx={{ wordBreak: 'break-all' }}>
                {urlRewritten
                  ? 'Facebook rewrote the search — this is what actually ran: '
                  : 'Searching the Ad Library with these filters: '}
                <Link href={searchUrl} target="_blank" rel="noreferrer">open it</Link>
              </Alert>
            )}

            {/* Stepper */}
            <Stepper activeStep={activeStep} orientation="vertical">
              {STEPS.map((label, index) => (
                <Step key={label} completed={activeStep > index}>
                  <StepLabel
                    error={isError && index === stageToStep(errorStage)}
                    StepIconProps={activeStep === index ? { icon: <CircularProgress size={20} /> } : undefined}
                  >
                    {label}
                    {index === 2 && adsFound > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({adsFound} ads found)
                      </Typography>
                    )}
                  </StepLabel>
                  {index === 2 && activeStep === 2 && (
                    <StepContent>
                      <Typography variant="caption" color="text.secondary">
                        Fetching from Facebook Ad Library...
                      </Typography>
                    </StepContent>
                  )}
                </Step>
              ))}
            </Stepper>

            {/* Error message */}
            {isError && (
              <Box sx={{ p: 2, bgcolor: 'error.main', color: 'error.contrastText', borderRadius: 1 }}>
                <Typography variant="body2">Fetch failed: {errorMessage}</Typography>
              </Box>
            )}

            {/* Done summary */}
            {isDone && (() => {
              const totalCollected = collectedAds.length;
              const refreshCount = refreshedCount || collectedAds.filter(a => a.urlRefreshed).length;
              const dupeCount = skippedCount || collectedAds.filter(a => a.isDuplicate && !a.urlRefreshed).length;
              const newCount = insertedCount || (totalCollected - dupeCount - refreshCount);
              return (
                <Box sx={{ p: 2, bgcolor: 'success.main', color: 'success.contrastText', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon />
                  <Typography variant="body2">
                    {newCount} new ads saved{refreshCount > 0 ? `, ${refreshCount} URL refreshed` : ''}, {dupeCount} already fetched — {totalCollected} total for "{keyword}"
                  </Typography>
                </Box>
              );
            })()}

            {/* Ad cards */}
            {collectedAds.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Ads Collected ({collectedAds.length})
                  {collectedAds.some(a => a.isDuplicate) && (() => {
                    const refreshN = collectedAds.filter(a => a.urlRefreshed).length;
                    const dupeN = collectedAds.filter(a => a.isDuplicate && !a.urlRefreshed).length;
                    const newN = collectedAds.length - dupeN - refreshN;
                    return (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({newN} new{refreshN > 0 ? `, ${refreshN} URL refreshed` : ''}, {dupeN} already fetched)
                      </Typography>
                    );
                  })()}
                </Typography>
                <Box sx={{ maxHeight: 400, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {collectedAds.map((ad, i) => (
                    <Card key={i} variant="outlined" sx={{ display: 'flex', minHeight: 100, ...(ad.urlRefreshed ? { bgcolor: 'rgba(2, 136, 209, 0.08)', borderColor: 'info.light' } : ad.isDuplicate ? { opacity: 0.55, bgcolor: 'action.hover' } : {}) }}>
                      {/* Thumbnail */}
                      <Box sx={{ width: 160, minWidth: 160, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                        {ad.videoThumbnailUrl ? (
                          <img src={ad.videoThumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <VideocamIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                        )}
                        {ad.videoUrl && (
                          <Box sx={{ position: 'absolute', bottom: 4, right: 4 }}>
                            <Chip label="Video" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
                          </Box>
                        )}
                      </Box>
                      {/* Content */}
                      <CardContent sx={{ flex: 1, py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            {ad.pageName || 'Unknown Page'}
                          </Typography>
                          {ad.urlRefreshed ? (
                            <Chip label="URL refreshed" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'info.light', color: 'info.contrastText' }} />
                          ) : ad.isDuplicate && (
                            <Chip label="Already fetched" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'warning.light' }} />
                          )}
                        </Box>
                        {ad.headline && (
                          <Typography variant="body2" color="text.primary" sx={{ mt: 0.25, lineHeight: 1.3 }}>
                            {ad.headline}
                          </Typography>
                        )}
                        {ad.caption && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400 }}>
                            {ad.caption}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          {ad.cta && <Chip label={ad.cta} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
                          {ad.startDate && (
                            <Typography variant="caption" color="text.secondary">{ad.startDate}</Typography>
                          )}
                          {ad.platforms && (
                            <Typography variant="caption" color="text.secondary">
                              {ad.platforms}
                            </Typography>
                          )}
                        </Box>
                        {ad.videoUrl && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              component="a"
                              href={ad.videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="caption"
                              color="primary"
                              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              Watch Video
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {showForm ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" disabled={!keyword.trim()} onClick={handleStartScrape}>
              Start Fetch
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={isRunning}>
              Close
            </Button>
            {isDone && !initialKeyword && !keywordSaved && (
              <Button
                variant="contained"
                startIcon={savingKeyword ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSaveKeyword}
                disabled={savingKeyword}
              >
                Save Keyword for Daily Fetching
              </Button>
            )}
            {keywordSaved && (
              <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} disabled>
                Keyword Saved
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
